import * as React from 'react';
import { Color, SpaceScale } from '../../theme/tokens';
import { useTheme } from '../../theme';
import { CSSRulesFunction, ResponsiveValue } from '../../theme/types';
import { omit } from '../../utils';
import classNames from 'classnames';

type HTMLProps = Omit<
  React.HTMLAttributes<SVGSVGElement>,
  keyof AppearanceProps & 'children'
>;

export enum LogoVariant {
  Inherit = 'Inherit',
  Default = 'Default',
  Primary = 'Primary',
  Grey = 'Grey',
}

interface AppearanceProps {
  size?: ResponsiveValue<SpaceScale>;
  logoVariant?: LogoVariant;
}

const logoColor: Record<LogoVariant, Color> = {
  Inherit: Color.Inherit,
  Default: Color.White,
  Primary: Color.Primary_600,
  Grey: Color.ColdGrey_800,
};

export interface LogoProps extends AppearanceProps, HTMLProps {}

const logoStyle: CSSRulesFunction<AppearanceProps> = (theme, props) => {
  const { logoVariant = Color.Inherit, size = 16 } = props;
  const color = theme.color(logoColor[logoVariant]);
  const scale = theme.responsiveSpacing(size, ['height']);

  return [
    {
      userSelect: 'none',
      overflow: 'hidden',
      flexShrink: 0,
      textAlign: 'center',
    },
    {
      color: color,
      svg: {
        fill: 'currentColor',
        stroke: 'currentColor',
      },
      path: {
        fill: 'currentColor',
        stroke: 'currentColor',
      },
    },
    scale,
  ];
};

const Logo: React.FC<LogoProps> = (props) => {
  const { theme } = useTheme();
  const rest = omit(props, ['size', 'logoVariant']);

  return (
    <svg
      viewBox="-1 0 108 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      css={logoStyle(theme, props)}
      className={classNames(rest.className, 'notranslate')}
      aria-hidden
      data-testid="logo"
      focusable={false}
    >
      <title>Gousto logo</title>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M85.5782 17.6002C85.3953 17.1429 85.2128 17.0059 84.938 17.0059C84.7554 17.0059 84.6184 17.0514 84.2981 17.1884C83.7038 17.4632 82.9266 17.6917 82.1953 17.6917C80.7326 17.6917 79.818 17.2347 79.818 15.6343V7.2684H84.2067C84.938 7.2684 85.2128 6.99439 85.2128 6.2627V4.80006C85.2128 4.06836 84.938 3.79435 84.2067 3.79435H79.818V1.0057C79.818 0.274012 79.544 0 78.8123 0H76.9838C76.2525 0 75.9781 0.274012 75.9781 1.0057V15.5429C75.9781 19.7031 78.4924 21.2572 81.9209 21.2572C83.201 21.2572 84.7555 20.8458 85.8522 20.2974C86.2181 20.1145 86.3555 19.9315 86.3555 19.7031C86.3555 19.4746 86.3096 19.3828 86.2181 19.1088L85.5782 17.6002ZM18.8803 2.33134C18.6063 2.05695 18.423 1.96587 18.1945 1.96587C17.9205 1.96587 17.7372 2.05695 17.4173 2.37688L14.6286 4.98298C13.1201 3.93136 11.246 3.33704 9.23427 3.33704C4.25129 3.33704 0.0914616 7.03993 0.0914616 12.2969C0.0914616 17.5543 4.25129 21.2572 9.23427 21.2572C14.2172 21.2572 18.3774 17.5543 18.3774 12.2969C18.3774 10.6514 17.9661 9.1428 17.2803 7.86309L20.1148 5.11999C20.4344 4.8456 20.4803 4.57159 20.4803 4.38829C20.4803 4.11428 20.3889 3.97727 20.0689 3.61143L18.8803 2.33134ZM30.3089 3.33704C25.3263 3.33704 21.1661 7.03993 21.1661 12.2969C21.1661 17.5543 25.3263 21.2572 30.3089 21.2572C35.2919 21.2572 39.4517 17.5543 39.4517 12.2969C39.4517 7.03993 35.2919 3.33704 30.3089 3.33704ZM67.8406 10.6058C65.4634 9.73712 64.4121 9.50865 64.4121 8.18264C64.4121 6.99439 65.3723 6.53708 66.6979 6.53708C67.7492 6.53708 68.7094 6.81147 69.8524 7.5887C70.0809 7.72571 70.2638 7.90863 70.5382 7.90863C70.7208 7.90863 70.9037 7.81717 71.0866 7.54279L71.9553 6.30861C72.1838 5.94277 72.2752 5.80576 72.2752 5.62284C72.2752 5.34845 72.1378 5.21145 71.8183 4.98298C70.3097 3.93136 68.8923 3.33704 66.5154 3.33704C63.5894 3.33704 60.7093 5.07407 60.7093 8.45703C60.7093 11.0628 62.0353 12.2514 65.3723 13.5315C67.4296 14.3087 69.0752 14.8571 69.0752 16.2746C69.0752 17.4632 68.1154 18.0575 66.6065 18.0575C65.2808 18.0575 64.0007 17.5543 62.5377 16.2287C62.3093 16.0457 62.1267 15.8628 61.852 15.8628C61.578 15.8628 61.4409 16.0002 61.2121 16.2746L60.1609 17.5087C60.0235 17.6917 59.8865 17.8742 59.8865 18.1031C59.8865 18.3315 60.0235 18.5144 60.2979 18.7888C61.8979 20.3889 64.0922 21.2572 66.5154 21.2572C70.4923 21.2572 72.9151 19.1088 72.9151 15.9543C72.9151 12.9372 70.8578 11.703 67.8406 10.6058ZM96.9154 3.33704C91.9324 3.33704 87.6811 7.03993 87.6811 12.2969C87.6811 17.5543 91.9324 21.2572 96.9154 21.2572C101.898 21.2572 106.15 17.5543 106.15 12.2969C106.15 7.03993 101.898 3.33704 96.9154 3.33704ZM56.5035 3.79435H54.675C53.9437 3.79435 53.6689 4.06836 53.6689 4.80006V13.7599C53.6689 16.5945 51.9778 17.6917 49.9205 17.6917C47.818 17.6917 46.1717 16.5945 46.1717 13.7599V4.80006C46.1717 4.06836 45.8977 3.79435 45.1664 3.79435H43.3379C42.6062 3.79435 42.3318 4.06836 42.3318 4.80006V13.7599C42.3318 18.6055 45.6237 21.2572 49.9205 21.2572C54.2177 21.2572 57.5096 18.6055 57.5096 13.7599V4.80006C57.5096 4.06836 57.2348 3.79435 56.5035 3.79435ZM9.23427 17.6917C6.2627 17.6917 3.84027 15.2685 3.84027 12.2969C3.84027 9.32573 6.2627 6.90293 9.23427 6.90293C12.2058 6.90293 14.6286 9.32573 14.6286 12.2969C14.6286 15.2685 12.2058 17.6917 9.23427 17.6917ZM30.3089 17.6917C27.3373 17.6917 24.9145 15.2685 24.9145 12.2969C24.9145 9.32573 27.3373 6.90293 30.3089 6.90293C33.2805 6.90293 35.7033 9.32573 35.7033 12.2969C35.7033 15.2685 33.2805 17.6917 30.3089 17.6917ZM96.9154 17.6917C93.9438 17.6917 91.521 15.2685 91.521 12.2969C91.521 9.32573 93.9438 6.90293 96.9154 6.90293C99.8869 6.90293 102.31 9.32573 102.31 12.2969C102.31 15.2685 99.8869 17.6917 96.9154 17.6917ZM17.5543 22.2629H15.6347C14.903 22.2629 14.6742 22.5373 14.6742 23.1771C14.5372 26.0572 12.2973 28.4345 9.23427 28.4345C6.2627 28.4345 3.93136 26.0572 3.79435 23.1771C3.79435 22.5373 3.56588 22.2629 2.83456 22.2629H0.914243C0.228467 22.2629 0 22.5373 0 23.1316C0 27.9316 4.02282 32 9.23427 32C14.4457 32 18.4685 27.9316 18.4685 23.1316C18.4685 22.5373 18.2401 22.2629 17.5543 22.2629Z"
        fill="white"
      />
    </svg>
  );
};

export default Logo;
